import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePost } from 'hooks';
import { generateFollowUps } from 'services/api';
import {
  getSearchRequest,
  getOrgInsightsSearchRequest,
  getSearchView,
  getFilters,
} from 'selectors/search';
import {
  getActiveAiSessionId,
  getAISearchData,
  getFollowUpQueries,
} from 'selectors/searchNLP';
import {
  ResearcherSearch,
  ActivitySearch,
  OrganisationSearch,
} from 'components/Search/Results/helpers';
import {
  followUpQueriesReceived,
  followUpQuerySuccessReceived,
  followUpQueryFailureReceived,
} from 'actions/searchNLP';

// UI-only filter keys that must not travel as part of the as-executed snapshot.
const TRANSIENT_FILTER_KEYS = [
  'aiFilterSessionId',
  'refreshSearch',
  'followUpQueries',
];

const entityTypeFor = view =>
  ResearcherSearch(view) ? 1 : ActivitySearch(view) ? 3 : 2;

const stripTransient = filters => {
  const out = { ...filters };
  TRANSIENT_FILTER_KEYS.forEach(key => delete out[key]);
  return out;
};

// A single result isn't worth refining, so insights are only "ready" above this many result rows.
// This counts grouped result rows (researchers/orgs/activities the user sees), not totalResults —
// totalResults is the raw record-match count, which is larger whenever a row groups many records
// (e.g. one researcher with several matching grants), so it would wrongly fetch for a single row.
const MINIMUM_REQUIRED_SEARCH_RESULTS = 1;
// Cap follow-up rounds per AI session so a long refinement chain can't fetch forever.
export const MAX_FOLLOWUP_ROUNDS = 3;

/**
 * Owns the follow-up query lifecycle: after a SciSearch AI session's insights land, fetch the
 * pre-computed additive follow-ups from the endpoint and store them in the searchNLP slice.
 *
 * The panel renders from the store, keyed by the AI session id and the as-executed filter
 * fingerprint, so it rehydrates after a remount without re-fetching and a refinement round (new
 * fingerprint) replaces the previous suggestions. The fetch is keyed on the identity of the
 * results object — the reference changes only when a search completes — so it fires regardless of
 * whether the tab was mounted when the search started.
 *
 * The "insights are ready" signal differs by vertical: researcher/activity searches write insights
 * to S3 inline, so a non-empty result set is the signal. Organisation search does not — a separate
 * insights call writes them (triggered eagerly in `components/Search`), so we wait for that payload.
 * Sessions resumed from a saved search never fetch: follow-up suggestions are tuned to a fresh
 * generation's interpretation, which a restored search doesn't carry. `resumedFromSavedSearch`
 * is returned so the panel can hide itself rather than show a "run a search" placeholder.
 * @param {{ enabled?: boolean }} [options] - when `enabled` is false no requests fire.
 * @returns {{ followUps: Array<{ text: string, filters: object }>, loading: boolean, isSearchRerunning: boolean, resumedFromSavedSearch: boolean, isCurrent: boolean, sessionId: string | null, searchFailed: boolean }}
 */
export const useFollowUpQueries = ({ enabled = true } = {}) => {
  const {
    res: searchResults,
    loading: searchLoading,
    error: searchError,
  } = useSelector(getSearchRequest);
  const { res: orgInsights, loading: orgInsightsLoading } = useSelector(
    getOrgInsightsSearchRequest
  );
  const sessionId = useSelector(getActiveAiSessionId);
  const aiSearchData = useSelector(getAISearchData);
  const view = useSelector(getSearchView);
  const filters = useSelector(getFilters);
  const stored = useSelector(getFollowUpQueries);
  const dispatch = useDispatch();

  const [{ res: followUpRes, loading: fetching }, fetchFollowUps] = usePost({
    url: generateFollowUps,
    configuration: {
      onRequestSuccess: response => {
        dispatch(followUpQuerySuccessReceived({ sessionId, response }));
      },
      onRequestError: () => {
        // A late error after the session moved on must not overwrite a newer round's state.
        if (requestSessionId.current !== sessionId) return;
        dispatch(
          followUpQueryFailureReceived({
            sessionId,
            fingerprint: requestFingerprint.current,
          })
        );
      },
    },
  });

  // Covers the whole round trip, not just the follow-up fetch: the search itself re-runs first
  // (org search's insights land via a separate call+flag), then the follow-up fetch starts once
  // results are ready. Combined so the panel can show one continuous "thinking" state throughout.
  const isSearchRerunning = OrganisationSearch(view)
    ? searchLoading || orgInsightsLoading
    : searchLoading;

  // The as-executed AI search identity, rebaselined on every follow-up apply, so it changes per
  // refinement round and is stable + serialisable for the store.
  const fingerprint = aiSearchData?.aiAppliedFingerprint ?? null;

  // SESSION_RESUMED stamps this marker on the record's provenance when an AI saved search is
  // loaded — the session is legitimate (insights, feedback) but has no generation behind it.
  const resumedFromSavedSearch = !!aiSearchData?.filterResponse
    ?.loadedFromSavedSearch;

  // The exact results object we last issued a fetch for. Identity changes per completed search, so
  // this is mount-independent and also stops the filters→results lead from firing before the new
  // result set has landed.
  const fetchedForResults = useRef(null);
  // Session + fingerprint the in-flight request was issued under, so a late response after either
  // changed is rejected rather than stored.
  const requestSessionId = useRef(null);
  const requestFingerprint = useRef(null);
  const cancelInFlight = useRef(null);

  // On session change, baseline the results on screen as already-handled and abandon any in-flight
  // fetch. A new session id is promoted before its own search runs, so the results still showing
  // belong to the previous session — only a fresh result set under the new session should fetch.
  const prevSessionId = useRef(sessionId);
  useEffect(() => {
    if (prevSessionId.current === sessionId) return;
    prevSessionId.current = sessionId;
    cancelInFlight.current?.();
    fetchedForResults.current = searchResults;
  }, [sessionId, searchResults]);

  // Org search gathers insights via a separate call, so it needs that payload too. Researcher /
  // Activity write insights inline, so a non-empty result set is the signal on its own.
  const insightsReady = OrganisationSearch(view)
    ? !!orgInsights &&
      Object.keys(orgInsights).length > 0 &&
      searchResults?.groupedResults?.length > MINIMUM_REQUIRED_SEARCH_RESULTS
    : searchResults?.groupedResults?.length > MINIMUM_REQUIRED_SEARCH_RESULTS;

  // Follow-ups for the search currently on screen. The store survives remounts, so the panel
  // rehydrates without re-fetching; a new session or refinement round (changed fingerprint) makes
  // this false and hides the previous round's chips for free.
  const isCurrent =
    !!sessionId &&
    stored.sessionId === sessionId &&
    stored.fingerprint === fingerprint;
  const followUps = isCurrent ? stored.queries : [];

  // Latches "busy" across the whole round trip — search re-run, then the follow-up fetch — so the
  // thinking indicator can stay mounted continuously instead of dropping out for the one render
  // between "search finished" and "the fetch effect below has actually fired", which would reset
  // its phrase-rotation state. Set true the instant either phase is genuinely running; only
  // cleared once this result set's outcome is known (chips landed, or a fetch was already
  // attempted/deliberately skipped for it) — read live inside the effect, after the session-reset
  // effect above, so a session switch's pre-emptive reset is seen before this decides not to clear.
  // Once the per-session cap is hit, clicking a chip still re-runs the search but the fetch effect
  // below early-returns without ever marking the results as fetched — so the latch must treat the
  // cap as "no fetch is coming" and clear, otherwise it spins forever on the last thinking phrase
  // and the max-rounds message only surfaces after a remount.
  const followUpRoundCapReached =
    stored.sessionId === sessionId && stored.count >= MAX_FOLLOWUP_ROUNDS;

  // A completed search with too few rows to refine never fires a fetch (see insightsReady) — so,
  // like the round cap, the latch must treat it as "no fetch is coming" and clear, otherwise the
  // thinking indicator hangs and the not-enough-results message never surfaces on the live search.
  // Gauged on the raw row count, not insightsReady, so it doesn't fire during org search's insights
  // gap (enough rows, insights still loading — where a fetch IS still coming).
  const resultsTooFewToFetch =
    !!searchResults?.groupedResults &&
    searchResults.groupedResults.length <= MINIMUM_REQUIRED_SEARCH_RESULTS;

  // A failed search clears its result set, so none of the signals above ever resolve for it: the
  // latch would spin on the last thinking phrase forever waiting for results that aren't coming.
  // Treat the error flag as a terminal outcome for the round instead. PERFORM_SEARCH resets it,
  // so a retry starts clean.
  const searchFailed = !!searchError;

  const [roundIsBusy, setRoundIsBusy] = useState(false);
  useEffect(() => {
    // A resumed saved search runs a real search (isSearchRerunning goes true) but never fetches
    // follow-ups, so the thinking indicator must never latch on for it — clear up front, like the
    // no-session case, so the "unavailable for saved searches" message shows instead of a spinner.
    if (!sessionId || resumedFromSavedSearch) {
      setRoundIsBusy(false);
      return;
    }
    if (isSearchRerunning || fetching) {
      setRoundIsBusy(true);
      return;
    }
    if (
      isCurrent ||
      fetchedForResults.current === searchResults ||
      followUpRoundCapReached ||
      resultsTooFewToFetch ||
      searchFailed
    ) {
      setRoundIsBusy(false);
    }
    // else: the search just finished but the fetch attempt below hasn't run yet — leave the latch
    // as-is (true, carried over from the running phase) until that effect resolves it either way.
  }, [
    sessionId,
    resumedFromSavedSearch,
    isSearchRerunning,
    fetching,
    isCurrent,
    searchResults,
    followUpRoundCapReached,
    resultsTooFewToFetch,
    searchFailed,
  ]);

  // Fetch once per search: skip if the store already holds this round (survives remount), if a new
  // result set hasn't landed yet, or if the per-session round cap is reached.
  useEffect(() => {
    if (
      !enabled ||
      resumedFromSavedSearch ||
      !sessionId ||
      searchFailed ||
      !insightsReady
    )
      return;
    if (isCurrent) return;
    if (fetchedForResults.current === searchResults) return;
    if (stored.sessionId === sessionId && stored.count >= MAX_FOLLOWUP_ROUNDS) {
      return;
    }

    fetchedForResults.current = searchResults;
    requestSessionId.current = sessionId;
    requestFingerprint.current = fingerprint;

    cancelInFlight.current?.();
    cancelInFlight.current = fetchFollowUps({
      body: {
        originalPrompt: aiSearchData?.naturalLanguageQuery?.queries?.[0] ?? '',
        sessionId,
        entityType: entityTypeFor(view),
        currentFilters: stripTransient(filters),
        synonymPluralPicks: {
          synonyms: aiSearchData?.filterResponse?.effectiveSynonyms ?? {},
          plurals: aiSearchData?.filterResponse?.effectivePlurals ?? {},
        },
      },
    });
  }, [
    enabled,
    resumedFromSavedSearch,
    sessionId,
    searchFailed,
    insightsReady,
    isCurrent,
    fingerprint,
    searchResults,
    stored,
    aiSearchData,
    view,
    filters,
    fetchFollowUps,
  ]);

  // Store the response, but only if it still matches the search + session we issued it for. This
  // runs on response arrival only (not on searchResults changes), so a newer search completing
  // can't re-store a stale payload; the guards reject a response captured against a moved-on search.
  useEffect(() => {
    if (!followUpRes) return;
    if (fetchedForResults.current !== searchResults) return;
    if (requestSessionId.current !== sessionId) return;
    if (followUpRes.errorCode) {
      // Infra failure delivered on a 200 (docs/adr/0001), e.g. a generation timeout.
      // Don't store it as a round: that would consume one of MAX_FOLLOWUP_ROUNDS and
      // tell the user no refinements exist, which isn't what happened.
      dispatch(
        followUpQueryFailureReceived({
          sessionId,
          fingerprint: requestFingerprint.current,
        })
      );
      return;
    }

    const queries = followUpRes.queries ?? [];
    dispatch(
      followUpQueriesReceived({
        sessionId,
        fingerprint: requestFingerprint.current,
        queries,
      })
    );
    // searchResults/fingerprint are read for the guard but intentionally excluded: this effect must
    // react only to a new response landing, not re-run when the search changes underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUpRes, sessionId, dispatch]);

  return {
    followUps,
    loading: roundIsBusy,
    // Lets the panel pick the "searching" vs "generating follow-ups" phrase list to match what's
    // actually happening, rather than blending both into one timer-driven rotation.
    isSearchRerunning,
    resumedFromSavedSearch,
    isCurrent,
    sessionId,
    searchFailed,
  };
};
